import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Building2, TrendingUp, CheckCircle, Clock, MapPin, HardHat, Plus, Home,
    ChevronDown, ChevronRight, Layers, AlertCircle, Search, Eye, Filter
} from 'lucide-react';
import { Card, CardStats, Button } from '@components/ui';
import { getInsaatIlerlemeOzeti, getBlokIlerlemOzeti, getBlokDaireleri, getDaireIlerleme, durumlar } from '@mocks/daireIlerleme';
import { mockBlocks, getBlocksByConstructionId } from '@mocks/bloklar';
import InsaatOlusturModal from './InsaatOlusturModal';
import clsx from 'clsx';

/**
 * İnşaat Projeleri — Birleşik Sayfa
 * Proje listesi + accordion drill-down (İnşaat → Blok → Daire)
 */
export default function InsaatProjeler() {
    const navigate = useNavigate();
    const { constructions, selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [modalOpen, setModalOpen] = useState(false);
    const [expandedInsaat, setExpandedInsaat] = useState(null);
    const [expandedBlok, setExpandedBlok] = useState(null);
    const [filterDurum, setFilterDurum] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Filtreleme
    const projeler = useMemo(() => {
        if (showAllConstructions) return constructions;
        return constructions.filter(p => p.id === selectedConstruction?.id);
    }, [constructions, selectedConstruction, showAllConstructions]);

    // Stats
    const toplamStats = useMemo(() => {
        let toplamDaire = 0, tamamlanan = 0, devamEden = 0, baslamamis = 0, toplamBlok = 0;
        projeler.forEach(ins => {
            toplamBlok += getBlocksByConstructionId(ins.id).length;
            const ozet = getInsaatIlerlemeOzeti(ins.id, mockBlocks);
            if (ozet) {
                toplamDaire += ozet.toplamDaire;
                tamamlanan += ozet.tamamlanan;
                devamEden += ozet.devamEden;
                baslamamis += ozet.baslamamis;
            }
        });
        return { toplamDaire, tamamlanan, devamEden, baslamamis, toplamBlok };
    }, [projeler]);

    const toplamProje = projeler.length;
    const aktifProje = projeler.filter(p => p.status === 'active').length;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">İnşaat Projeleri</h1>
                    <p className="text-text-secondary text-sm mt-1">
                        {showAllConstructions ? 'Tüm projeler' : selectedConstruction?.name} • Daire bazlı ilerleme durumları
                    </p>
                </div>
                <Button variant="primary" icon={<Plus size={18} />} onClick={() => setModalOpen(true)}>
                    Yeni İnşaat
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <CardStats title="Toplam Proje" value={toplamProje} icon={Building2} color="primary" size="sm" />
                <CardStats title="Toplam Daire" value={toplamStats.toplamDaire} icon={Home} color="info" size="sm" />
                <CardStats title="Tamamlanan" value={toplamStats.tamamlanan} icon={CheckCircle} color="success" size="sm" />
                <CardStats title="Devam Eden" value={toplamStats.devamEden} icon={Clock} color="warning" size="sm" />
                <CardStats title="Başlanmadı" value={toplamStats.baslamamis} icon={AlertCircle} color="tertiary" size="sm" />
            </div>

            {/* Filtre */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                        type="text"
                        placeholder="Daire veya blok ara..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                    />
                </div>
                <select
                    value={filterDurum}
                    onChange={e => setFilterDurum(e.target.value)}
                    className="px-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                >
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(durumlar).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                    ))}
                </select>
            </div>

            {/* Proje Listesi — Accordion */}
            <div className="space-y-4">
                {projeler.map(insaat => (
                    <InsaatAccordion
                        key={insaat.id}
                        insaat={insaat}
                        isExpanded={expandedInsaat === insaat.id}
                        onToggle={() => setExpandedInsaat(expandedInsaat === insaat.id ? null : insaat.id)}
                        expandedBlok={expandedBlok}
                        setExpandedBlok={setExpandedBlok}
                        filterDurum={filterDurum}
                        searchTerm={searchTerm}
                        navigate={navigate}
                    />
                ))}
            </div>

            {projeler.length === 0 && (
                <Card className="text-center py-12">
                    <Building2 size={48} className="mx-auto text-text-muted mb-4" />
                    <h3 className="text-lg font-medium text-text-primary mb-2">Proje Bulunamadı</h3>
                    <p className="text-text-secondary">Seçili inşaat için proje verisi yok.</p>
                </Card>
            )}

            {/* Modal */}
            <InsaatOlusturModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
        </div>
    );
}

// ============================================
// Renk yardımcıları
// ============================================
const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
const getProgressTextColor = (y) => y >= 75 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

const getStatusBadge = (status) => {
    switch (status) {
        case 'active': return { label: 'Devam Ediyor', color: 'bg-success/10 text-success' };
        case 'completed': return { label: 'Tamamlandı', color: 'bg-info/10 text-info' };
        case 'planning': return { label: 'Planlama', color: 'bg-warning/10 text-warning' };
        default: return { label: status, color: 'bg-bg-tertiary text-text-muted' };
    }
};

// ============================================
// İnşaat Accordion Kartı
// ============================================
function InsaatAccordion({
    insaat, isExpanded, onToggle, expandedBlok, setExpandedBlok,
    filterDurum, searchTerm, navigate
}) {
    const bloklar = useMemo(() => getBlocksByConstructionId(insaat.id), [insaat.id]);
    const ozet = useMemo(() => getInsaatIlerlemeOzeti(insaat.id, mockBlocks), [insaat.id]);
    const ilerleme = ozet?.genelIlerleme || insaat.progress || 0;
    const status = getStatusBadge(insaat.status);

    return (
        <Card padding="none" className={clsx('overflow-hidden transition-all', isExpanded && 'ring-1 ring-primary/20')}>
            {/* Üst progress çizgi */}
            <div className="h-1 bg-bg-tertiary">
                <div className={clsx('h-full', getProgressColor(ilerleme))} style={{ width: `${ilerleme}%` }} />
            </div>

            {/* Header */}
            <div className="flex items-center">
                <button
                    onClick={onToggle}
                    className="flex-1 flex items-center gap-4 p-4 md:p-5 text-left hover:bg-bg-hover transition-colors"
                >
                    <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
                        <Building2 size={22} className="text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-text-primary text-base">{insaat.name}</h3>
                            <span className={clsx('px-2 py-0.5 rounded-full text-2xs font-medium', status.color)}>{status.label}</span>
                        </div>
                        <p className="text-xs text-text-muted flex items-center gap-1 mt-0.5">
                            <MapPin size={12} />
                            {insaat.district}, {insaat.city}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        {/* Blok / Daire / Durum bilgisi */}
                        <div className="hidden md:flex items-center gap-3 text-xs text-text-muted mr-2">
                            <span>{bloklar.length} Blok</span>
                            <span>•</span>
                            <span>{ozet?.toplamDaire || 0} Daire</span>
                        </div>
                        {ozet && (
                            <div className="hidden sm:flex items-center gap-1.5 text-xs mr-1">
                                <span className="text-success font-medium">{ozet.tamamlanan}</span>
                                <span className="text-text-muted">/</span>
                                <span className="text-warning font-medium">{ozet.devamEden}</span>
                                <span className="text-text-muted">/</span>
                                <span className="text-text-muted">{ozet.baslamamis}</span>
                            </div>
                        )}
                        <span className={clsx('text-xl font-bold', getProgressTextColor(ilerleme))}>%{ilerleme}</span>
                        <ChevronDown size={18} className={clsx('text-text-muted transition-transform', isExpanded && 'rotate-180')} />
                    </div>
                </button>

                {/* Detay butonu */}
                <button
                    onClick={() => navigate(`/insaat/projeler/${insaat.id}`)}
                    className="p-3 mr-2 rounded-lg hover:bg-primary/10 text-text-muted hover:text-primary transition-colors"
                    title="Proje Detay"
                >
                    <Eye size={18} />
                </button>
            </div>

            {/* Genişletilmiş — blok listesi */}
            {isExpanded && (
                <div className="border-t border-border-light bg-bg-primary/50">
                    {bloklar.length === 0 ? (
                        <p className="text-center text-sm text-text-muted py-6">Bu inşaatta blok bulunamadı</p>
                    ) : (
                        <div className="divide-y divide-border-light/50">
                            {bloklar.map(blok => (
                                <BlokAccordion
                                    key={blok.id}
                                    blok={blok}
                                    insaatId={insaat.id}
                                    isExpanded={expandedBlok === blok.id}
                                    onToggle={() => setExpandedBlok(expandedBlok === blok.id ? null : blok.id)}
                                    filterDurum={filterDurum}
                                    searchTerm={searchTerm}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}

// ============================================
// Blok Accordion (İnşaat genişletildiğinde)
// ============================================
function BlokAccordion({
    blok, insaatId, isExpanded, onToggle,
    filterDurum, searchTerm, navigate
}) {
    const blokOzet = useMemo(() => getBlokIlerlemOzeti(blok.id), [blok.id]);
    const ilerleme = blokOzet?.genelIlerleme || 0;

    // Daire listesi
    const daireler = useMemo(() => {
        const blokData = getBlokDaireleri(blok.id);
        if (!blokData) return [];
        const list = [];
        blokData.katlar.forEach(kat => {
            kat.daireler.forEach(daire => {
                const il = getDaireIlerleme(daire.id);
                list.push({
                    ...daire,
                    genelIlerleme: il?.genelIlerleme || 0,
                    durum: il?.durum || 'baslanmadi',
                });
            });
        });
        return list;
    }, [blok.id]);

    // Filtreleme
    const filteredDaireler = useMemo(() => {
        let list = daireler;
        if (filterDurum) list = list.filter(d => d.durum === filterDurum);
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(d => d.no.toLowerCase().includes(s) || d.tip.toLowerCase().includes(s));
        }
        return list;
    }, [daireler, filterDurum, searchTerm]);

    return (
        <div className="pl-4 md:pl-8">
            {/* Blok Header */}
            <button
                onClick={onToggle}
                className="w-full flex items-center gap-3 py-3 pr-4 text-left hover:bg-bg-hover/50 transition-colors"
            >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Layers size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-text-primary">{blok.name}</span>
                    <span className="text-xs text-text-muted ml-2">{blok.totalFloors} Kat • {blok.totalApartments} Daire</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="w-16 h-1.5 bg-bg-tertiary rounded-full overflow-hidden hidden sm:block">
                        <div className={clsx('h-full rounded-full', getProgressColor(ilerleme))} style={{ width: `${ilerleme}%` }} />
                    </div>
                    <span className={clsx('text-sm font-bold', getProgressTextColor(ilerleme))}>%{ilerleme}</span>
                    <ChevronDown size={14} className={clsx('text-text-muted transition-transform', isExpanded && 'rotate-180')} />
                </div>
            </button>

            {/* Genişletilmiş — daire grid */}
            {isExpanded && (
                <div className="pb-4 pr-4">
                    {filteredDaireler.length === 0 ? (
                        <p className="text-center text-xs text-text-muted py-4">Daire bulunamadı</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {filteredDaireler.map(daire => {
                                const d = durumlar[daire.durum];
                                return (
                                    <button
                                        key={daire.id}
                                        onClick={() => navigate(`/insaat/projeler/${insaatId}/blok/${blok.id}/daire/${daire.id}`)}
                                        className={clsx(
                                            'flex flex-col p-2.5 rounded-xl border text-left transition-all',
                                            'hover:shadow-md hover:border-primary/30 active:scale-[0.97]',
                                            daire.durum === 'tamamlandi' ? 'border-success/20 bg-success/5' :
                                                daire.durum === 'baslanmadi' ? 'border-border-light bg-bg-tertiary/30' :
                                                    'border-border-light bg-bg-primary'
                                        )}
                                    >
                                        <div className="flex items-center justify-between w-full mb-1">
                                            <span className="text-xs font-bold text-text-primary">{daire.no}</span>
                                            <span className={clsx('text-2xs font-bold', getProgressTextColor(daire.genelIlerleme))}>
                                                %{daire.genelIlerleme}
                                            </span>
                                        </div>
                                        <div className="h-1 w-full bg-bg-tertiary rounded-full overflow-hidden mb-1">
                                            <div
                                                className={clsx('h-full rounded-full', getProgressColor(daire.genelIlerleme))}
                                                style={{ width: `${daire.genelIlerleme}%` }}
                                            />
                                        </div>
                                        <span className={clsx('text-2xs', d.renk)}>{d.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Blok detay linki */}
                    <div className="mt-2 text-right">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/insaat/projeler/${insaatId}/blok/${blok.id}`)}
                        >
                            Blok Detay <ChevronRight size={14} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
